import { useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, IconButton, LinearProgress, MenuItem, Pagination, Paper, Snackbar, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, TextField, Tooltip, Typography, useTheme,
} from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import CodeRoundedIcon from '@mui/icons-material/CodeRounded';
import CloudDownloadRoundedIcon from '@mui/icons-material/CloudDownloadRounded';
import PictureAsPdfRoundedIcon from '@mui/icons-material/PictureAsPdfRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import VerifiedRoundedIcon from '@mui/icons-material/VerifiedRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import dayjs, { Dayjs } from 'dayjs';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { LinhasEsqueletoTabela, paperListagemSx, tableContainerListagemSx } from '../../../shared/components';
import { CelulaListaMobile, LinhaListagemMobile, ListaMobile, moedaMobile, useCelular } from '../../../shared/components/listagem-mobile';
import { Environment } from '../../../shared/environment';
import { useDebounce, useListagemAltura } from '../../../shared/hooks';
import { LayoutBaseDePagina } from '../../../shared/layouts';
import { NfeServiceDistribuidas } from '../../../shared/services/api/Financeiro/fiscaldistribuidas';
import { INotaCompra, NotasCompraService } from '../../../shared/services/api/Fiscal/NotasCompraService';
import { DialogVerificarNotasCompra } from './DialogVerificarNotasCompra';

const OPCOES_SITUACAO = [
  { valor: '', rotulo: 'Todas' },
  { valor: 'normal', rotulo: 'Normais' },
  { valor: 'cancelada', rotulo: 'Canceladas' },
  { valor: 'denegada', rotulo: 'Denegadas' },
  { valor: 'irregular', rotulo: 'Irregulares' },
  { valor: 'naoverificada', rotulo: 'Não verificadas' },
];

const COLUNAS = 7;
const SEM_MANIFESTACAO = '000000';

const SITUACOES = {
  normal: { rotulo: 'Normal', cor: 'success' },
  cancelada: { rotulo: 'Cancelada', cor: 'error' },
  denegada: { rotulo: 'Denegada', cor: 'warning' },
  irregular: { rotulo: 'Irregular', cor: 'warning' },
} as const;

const formatarCnpj = (valor: string) => {
  const d = (valor || '').replace(/\D/g, '');
  if (d.length === 14) return d.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
  if (d.length === 11) return d.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
  return valor || '';
};

const documentoDaNota = (nota: INotaCompra) => {
  if (nota.lancada) return { rotulo: 'Lançada', cor: 'info' as const };
  if (nota.idnfe) return { rotulo: 'XML disponível', cor: 'default' as const };
  return { rotulo: 'Só resumo', cor: 'default' as const };
};

export const ListagemDeNotasCompra: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const listaCelular = useCelular();
  const { debounce } = useDebounce();
  const [searchParams, setSearchParams] = useSearchParams();

  const [rows, setRows] = useState<INotaCompra[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [recarregar, setRecarregar] = useState(0);
  const [verificando, setVerificando] = useState<string | null>(null);
  const [baixando, setBaixando] = useState<string | null>(null);
  const [verificarAberto, setVerificarAberto] = useState(false);
  const [mensagem, setMensagem] = useState<{ texto: string; tipo: 'success' | 'error' | 'info' } | null>(null);

  const { tabelaRef, containerHeight, rowsPerPage } = useListagemAltura({
    cacheKey: 'notasCompra',
    valorInicial: Environment.LIMITE_DE_LINHASSMALL,
    rowHeight: 49,
    footerReservado: 60,
    bottomGap: 24,
  });

  const busca = searchParams.get('busca') || '';
  const situacao = searchParams.get('situacao') || '';
  const pagina = Number(searchParams.get('pagina') || '1');
  const dataInicial = useMemo(() => (searchParams.has('datainicial')
    ? searchParams.get('datainicial') || ''
    : dayjs().startOf('month').format('YYYY-MM-DD')), [searchParams]);
  const dataFinal = useMemo(() => (searchParams.has('datafinal')
    ? searchParams.get('datafinal') || ''
    : dayjs().format('YYYY-MM-DD')), [searchParams]);

  const mudarFiltros = (novo: Record<string, string>) => setSearchParams({
    busca, situacao, datainicial: dataInicial, datafinal: dataFinal, pagina: '1', ...novo,
  }, { replace: true });

  useEffect(() => {
    setIsLoading(true);
    debounce(() => {
      NotasCompraService.getAll({ pagina, limite: rowsPerPage, busca, situacao, dataInicial, dataFinal }).then((result) => {
        setIsLoading(false);
        if (result instanceof Error) {
          setMensagem({ texto: result.message, tipo: 'error' });
          return;
        }
        setRows(result.data);
        setTotalCount(result.totalCount);
      });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagina, rowsPerPage, busca, situacao, dataInicial, dataFinal, recarregar]);

  const consultar = async (nota: INotaCompra) => {
    setVerificando(nota.chave);
    const result = await NotasCompraService.consultarSituacao(nota.chave);
    setVerificando(null);
    if (result instanceof Error) return result;
    setRows((atual) => atual.map((r) => (r.chave === nota.chave ? { ...r, ...result, verificar: false } : r)));
    return null;
  };

  const baixar = async (id: string, acao: () => Promise<void | Error>) => {
    setBaixando(id);
    const erro = await acao();
    setBaixando(null);
    if (erro) setMensagem({ texto: erro.message, tipo: 'error' });
  };

  const buscarXml = async (nota: INotaCompra) => {
    if (nota.manifestacao === SEM_MANIFESTACAO) {
      setMensagem({ texto: 'Dê primeiro a Ciência da Operação em NFE Dist: sem manifestação a SEFAZ não libera o XML.', tipo: 'info' });
      navigate('/nfedist');
      return;
    }
    setBaixando(`buscar-${nota.chave}`);
    const result = await NfeServiceDistribuidas.baixarXml(nota.idresnfe || '');
    setBaixando(null);
    setMensagem(result instanceof Error ? { texto: result.message, tipo: 'error' } : { texto: result, tipo: 'success' });
    if (!(result instanceof Error)) setRecarregar((n) => n + 1);
  };

  const mesSelecionado = (dataInicial ? dayjs(dataInicial) : dayjs()).format('YYYY-MM');
  const totalPaginas = Math.max(1, Math.ceil(totalCount / rowsPerPage));

  const chipSituacao = (nota: INotaCompra) => {
    if (verificando === nota.chave) return <Chip size="small" variant="outlined" label="Consultando..." />;
    if (!nota.situacao) return <Chip size="small" variant="outlined" label="Não verificada" />;
    const info = SITUACOES[nota.situacao];
    return (
      <Tooltip title={nota.dhconsulta ? `Consultada na SEFAZ em ${dayjs(nota.dhconsulta).format('DD/MM/YYYY HH:mm')}${nota.xmotivo ? ` — ${nota.xmotivo}` : ''}` : ''}>
        <Chip size="small" color={info.cor} label={info.rotulo} />
      </Tooltip>
    );
  };

  const acoes = (nota: INotaCompra) => (
    <Box display="flex" justifyContent="flex-end" onClick={(e) => e.stopPropagation()}>
      {nota.idnfe ? (
        <>
          <Tooltip title="Baixar XML">
            <span>
              <IconButton size="small" aria-label="Baixar XML" disabled={!!baixando} onClick={() => baixar(`xml-${nota.chave}`, () => NotasCompraService.baixarXml(nota))}>
                {baixando === `xml-${nota.chave}` ? <CircularProgress size={18} /> : <CodeRoundedIcon fontSize="small" />}
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Baixar DANFE (PDF)">
            <span>
              <IconButton size="small" aria-label="Baixar DANFE" disabled={!!baixando} onClick={() => baixar(`pdf-${nota.chave}`, () => NotasCompraService.baixarDanfe(nota))}>
                {baixando === `pdf-${nota.chave}` ? <CircularProgress size={18} /> : <PictureAsPdfRoundedIcon fontSize="small" />}
              </IconButton>
            </span>
          </Tooltip>
        </>
      ) : nota.situacao !== 'cancelada' && (
        <Tooltip title="Buscar o XML completo na SEFAZ">
          <span>
            <IconButton size="small" aria-label="Buscar XML na SEFAZ" disabled={!!baixando} onClick={() => buscarXml(nota)}>
              {baixando === `buscar-${nota.chave}` ? <CircularProgress size={18} /> : <CloudDownloadRoundedIcon fontSize="small" />}
            </IconButton>
          </span>
        </Tooltip>
      )}
      {/* Celular: não cabe o terceiro botão, e a conferência já é automática. */}
      {!listaCelular && nota.situacao !== 'cancelada' && nota.situacao !== 'denegada' && (
        <Tooltip title="Consultar a situação na SEFAZ agora">
          <span>
            <IconButton
              size="small"
              aria-label="Consultar situação"
              disabled={!!verificando}
              onClick={async () => {
                const erro = await consultar(nota);
                if (erro) setMensagem({ texto: erro.message, tipo: 'error' });
              }}
            >
              <RefreshRoundedIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      )}
    </Box>
  );

  return (
    <LayoutBaseDePagina
      titulo="Notas de Compra"
      subtitulo="Notas recebidas da SEFAZ contra a empresa, com a situação de cada uma (normal ou cancelada)."
      barraDeFerramentas={
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <Box component={Paper} variant="outlined" sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, mx: 1, mb: 2, p: 1.5 }}>
            <TextField
              size="small"
              placeholder="Fornecedor, CNPJ, número ou chave"
              value={busca}
              onChange={(e) => mudarFiltros({ busca: e.target.value })}
              InputProps={{ startAdornment: <SearchRoundedIcon fontSize="small" sx={{ mr: 1, color: theme.torq.text.muted }} /> }}
              sx={{ flex: '1 1 260px' }}
            />
            <DatePicker<Dayjs>
              label="Emissão de"
              format="DD/MM/YYYY"
              value={dataInicial ? dayjs(dataInicial) : null}
              onChange={(d) => mudarFiltros({ datainicial: d && d.isValid() ? d.format('YYYY-MM-DD') : '' })}
              slotProps={{ textField: { size: 'small', sx: { width: 160 } } }}
            />
            <DatePicker<Dayjs>
              label="Até"
              format="DD/MM/YYYY"
              value={dataFinal ? dayjs(dataFinal) : null}
              onChange={(d) => mudarFiltros({ datafinal: d && d.isValid() ? d.format('YYYY-MM-DD') : '' })}
              slotProps={{ textField: { size: 'small', sx: { width: 160 } } }}
            />
            <TextField select size="small" label="Situação" value={situacao} onChange={(e) => mudarFiltros({ situacao: e.target.value })} sx={{ width: 170 }}>
              {OPCOES_SITUACAO.map((o) => <MenuItem key={o.valor} value={o.valor}>{o.rotulo}</MenuItem>)}
            </TextField>
            <Button variant="contained" startIcon={<VerifiedRoundedIcon />} onClick={() => setVerificarAberto(true)}>
              Verificar notas
            </Button>
          </Box>
        </LocalizationProvider>
      }
    >
      <DialogVerificarNotasCompra
        open={verificarAberto}
        mesInicial={mesSelecionado}
        onClose={() => setVerificarAberto(false)}
        onVerificou={() => setRecarregar((n) => n + 1)}
      />

      <Snackbar
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        open={!!mensagem}
        autoHideDuration={mensagem?.tipo === 'error' ? null : 6000}
        onClose={(_, motivo) => { if (motivo !== 'clickaway') setMensagem(null); }}
      >
        <Alert severity={mensagem?.tipo} variant="filled" onClose={() => setMensagem(null)} sx={{ width: '100%' }}>
          {mensagem?.texto}
        </Alert>
      </Snackbar>

      <Paper elevation={0} sx={{ ...paperListagemSx(theme), mx: { xs: 1, sm: 0 } }}>
        <TableContainer ref={tabelaRef} sx={tableContainerListagemSx(containerHeight && containerHeight - 60, rows.length >= rowsPerPage)}>
          <Table size="small" sx={{ minWidth: listaCelular ? 0 : 860 }}>
            {!listaCelular && (
              <TableHead sx={{ position: 'sticky', top: 0, zIndex: 2 }}>
                <TableRow>
                  <TableCell sx={{ width: 104 }}>Emissão</TableCell>
                  <TableCell sx={{ width: 90 }}>Número</TableCell>
                  <TableCell>Fornecedor</TableCell>
                  <TableCell align="right" sx={{ width: 120 }}>Valor</TableCell>
                  <TableCell sx={{ width: 120 }}>Situação</TableCell>
                  <TableCell sx={{ width: 130 }}>Documento</TableCell>
                  <TableCell align="right" sx={{ width: 120 }}>Baixar</TableCell>
                </TableRow>
              </TableHead>
            )}
            <TableBody>
              {isLoading && rows.length === 0 && !listaCelular && <LinhasEsqueletoTabela colunas={Array.from({ length: COLUNAS }, () => ({}))} linhas={8} />}
              {listaCelular ? (
                <TableRow>
                  <CelulaListaMobile>
                    <ListaMobile
                      linhas={rows}
                      carregando={isLoading}
                      chave={(nota) => nota.chave}
                      vazio="Nenhuma nota encontrada"
                      renderizar={(nota) => (
                        <LinhaListagemMobile
                          titulo={nota.nome || 'Fornecedor não informado'}
                          detalhes={[dayjs(nota.dhemi).format('DD/MM/YY'), `Nº ${nota.numero}`, documentoDaNota(nota).rotulo]}
                          valor={moedaMobile(nota.valor)}
                          corValor={nota.situacao === 'cancelada' ? 'text.disabled' : 'text.primary'}
                          etiqueta={{
                            rotulo: nota.situacao ? SITUACOES[nota.situacao].rotulo : 'Não verificada',
                            cor: nota.situacao ? SITUACOES[nota.situacao].cor : 'default',
                          }}
                          destaque={nota.situacao === 'cancelada'}
                          cor={nota.situacao === 'cancelada' ? theme.palette.error.main : undefined}
                          acoes={acoes(nota)}
                        />
                      )}
                    />
                  </CelulaListaMobile>
                </TableRow>
              ) : rows.map((nota) => {
                const documento = documentoDaNota(nota);
                const cancelada = nota.situacao === 'cancelada';
                return (
                  <TableRow key={nota.chave} hover sx={{ opacity: cancelada ? 0.75 : 1 }}>
                    <TableCell>{dayjs(nota.dhemi).format('DD/MM/YYYY')}</TableCell>
                    <TableCell>{nota.numero}</TableCell>
                    <TableCell>
                      <Typography noWrap sx={{ fontSize: '0.8125rem', fontWeight: 600, maxWidth: 420 }}>{nota.nome || 'Fornecedor não informado'}</Typography>
                      <Typography noWrap sx={{ fontSize: '0.72rem', color: 'text.secondary' }}>{formatarCnpj(nota.cnpj)}</Typography>
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600, whiteSpace: 'nowrap', ...(cancelada && { textDecoration: 'line-through', color: 'text.secondary' }) }}>
                      {Number(nota.valor || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                    </TableCell>
                    <TableCell>{chipSituacao(nota)}</TableCell>
                    <TableCell><Chip size="small" variant="outlined" color={documento.cor} label={documento.rotulo} /></TableCell>
                    <TableCell align="right" sx={{ py: 0 }}>{acoes(nota)}</TableCell>
                  </TableRow>
                );
              })}
              {!isLoading && rows.length === 0 && !listaCelular && (
                <TableRow>
                  <TableCell colSpan={COLUNAS} align="center" sx={{ py: 5, color: 'text.secondary' }}>
                    Nenhuma nota encontrada para esse filtro.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {isLoading && <LinearProgress sx={{ height: 2 }} />}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, px: 2, py: 1, borderTop: `1px solid ${theme.torq.border.default}` }}>
          <Typography variant="caption" color="text.secondary">
            {totalCount === 0 ? 'Nenhuma nota' : `${totalCount} nota${totalCount !== 1 ? 's' : ''}`}
          </Typography>
          {totalCount > rowsPerPage && (
            <Pagination
              page={pagina}
              count={totalPaginas}
              size="small"
              shape="rounded"
              showFirstButton
              showLastButton
              onChange={(_, nova) => mudarFiltros({ pagina: String(nova) })}
            />
          )}
        </Box>
      </Paper>
    </LayoutBaseDePagina>
  );
};
